import type { FrameSpec } from './frames';
import SHOPIFY_SHAPES from './shopify-shapes.json';

/**
 * A200 — the two try-on collections. The data line `Classic` is the « Signature » collection of
 * maisonmata.com (tag `Classic`, product type `Signature`); `Handmade` keeps its name. They are
 * never mixed in one list: each has its own catalogue page and its own model list, with a switch
 * between them.
 */
export type CatalogueLine = 'Handmade' | 'Classic';
export const LINES: readonly CatalogueLine[] = ['Handmade', 'Classic'];

/** Public name of a line (never translated: it is the collection's name on the site). */
export function lineName(line: CatalogueLine): string {
  return line === 'Classic' ? 'Signature' : 'Handmade';
}

/** `?collection=` / page value of a line: « handmade » or « signature ». */
export function lineSlug(line: CatalogueLine): 'handmade' | 'signature' {
  return line === 'Classic' ? 'signature' : 'handmade';
}

export function lineFromSlug(value: string | null | undefined): CatalogueLine | undefined {
  const v = (value ?? '').toLowerCase();
  if (v === 'signature' || v === 'classic') return 'Classic';
  if (v === 'handmade') return 'Handmade';
  return undefined;
}

export function isLine(value: unknown): value is CatalogueLine {
  return value === 'Handmade' || value === 'Classic';
}

/**
 * Frame shapes, in filter order: the values of the Shopify « Forme » field (`custom.shape`),
 * which also builds the shape collections of the site (« Round Sunglasses »…).
 */
export const SHAPES = ['Rectangle', 'Cat-eye', 'Oval', 'Aviator', 'Round', 'Butterfly', 'Square', 'Hexagonal', 'Oversized', 'Sculptural'] as const;
export type Shape = typeof SHAPES[number];

const SHAPES_BY_FRAME = (SHOPIFY_SHAPES as { shapes: Record<string, string[]> }).shapes;

/** Shapes of a frame (a frame can have several, as on the site). Unknown frame: none. */
export function shapesOf(frame: Pick<FrameSpec, 'id'>): Shape[] {
  return (SHAPES_BY_FRAME[frame.id] ?? []).filter((s): s is Shape => (SHAPES as readonly string[]).includes(s));
}

/** Shapes present in a list of frames, in filter order. */
export function shapesIn(frames: readonly Pick<FrameSpec, 'id'>[]): Shape[] {
  const present = new Set(frames.flatMap(f => shapesOf(f)));
  return SHAPES.filter(s => present.has(s));
}

export function hasShape(frame: Pick<FrameSpec, 'id'>, shape: string | null | undefined): boolean {
  return !shape || shape === 'all' || shapesOf(frame).includes(shape as Shape);
}

export function isShape(value: string | null | undefined): value is Shape {
  return (SHAPES as readonly string[]).includes(value ?? '');
}
